import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { Modal } from './Modal';
import { Button } from './Button';

/**
 * ConfirmDialog Component
 *
 * Reusable confirmation dialog for destructive or important actions.
 * Built on top of the Modal wrapper with standardized confirm/cancel pattern.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {Function} props.onConfirm - Callback when confirm button is clicked
 * @param {string} props.title - Dialog title
 * @param {string} props.message - Confirmation message
 * @param {string} props.confirmText - Text for confirm button (default: "Confirm")
 * @param {string} props.cancelText - Text for cancel button (default: "Cancel")
 * @param {string} props.variant - Button variant: 'danger', 'primary', 'secondary' (default: 'danger')
 * @param {string} props.style - Dialog style: 'success', 'danger', 'warning', 'info' (default: 'danger')
 * @param {boolean} props.isLoading - Whether the confirm action is in progress
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant,
  style = 'danger',
  isLoading = false,
}) {
  const handleConfirm = () => {
    onConfirm();
  };

  // Style configuration
  const styleConfig = {
    success: {
      icon: 'lucide:check-circle-2',
      iconColor: 'text-green-500',
      iconBg: 'bg-green-50',
      buttonVariant: 'primary',
    },
    danger: {
      icon: 'lucide:alert-circle',
      iconColor: 'text-red-500',
      iconBg: 'bg-red-50',
      buttonVariant: 'danger',
    },
    warning: {
      icon: 'lucide:alert-triangle',
      iconColor: 'text-yellow-500',
      iconBg: 'bg-yellow-50',
      buttonVariant: 'primary',
    },
    info: {
      icon: 'lucide:info',
      iconColor: 'text-brand',
      iconBg: 'bg-brand/10',
      buttonVariant: 'primary',
    },
  };

  const config = styleConfig[style] || styleConfig.danger;
  const buttonVariant = variant !== undefined ? variant : config.buttonVariant;

  return (
    <Modal isOpen={open} onClose={onClose} title={title} size="sm">
      {/* Icon */}
      <div className="flex justify-center py-4">
        <div className={`${config.iconColor}`}>
          <Icon icon={config.icon} className="size-10" />
        </div>
      </div>

      {/* Message */}
      <div className="px-4 sm:px-7 py-4">
        <p className="text-center text-base text-subtle">
          {message}
        </p>
      </div>

      {/* Actions */}
      <div className="pt-4 border-t border-border-default flex gap-3 justify-end">
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isLoading}
        >
          {cancelText}
        </Button>
        <Button
          variant={buttonVariant}
          onClick={handleConfirm}
          isLoading={isLoading}
          disabled={isLoading}
        >
          {confirmText}
        </Button>
      </div>
    </Modal>
  );
}

ConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  variant: PropTypes.oneOf(['danger', 'primary', 'secondary']),
  style: PropTypes.oneOf(['success', 'danger', 'warning', 'info']),
  isLoading: PropTypes.bool,
};
